import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, switchMap } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { RulesApiService, ComplianceLockInfo, PreviewResult } from 'src/app/core/services/rules-api.service';
import { RuleConfigEditorComponent } from '../rule-config-editor/rule-config-editor.component';

@Component({
  selector: 'app-rule-packs-edit',
  standalone: true,
  imports: [CommonModule, SharedModule, IconModule, RuleConfigEditorComponent],
  templateUrl: './rule-packs-edit.component.html',
})
export class RulePacksEditComponent implements OnInit {
  private readonly rulesApi = inject(RulesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  tenantId!: number;
  productId!: number;
  country = '';

  config: Record<string, unknown> | null = null;
  locks: ComplianceLockInfo[] = [];
  get lockedKeys(): string[] {
    return this.locks.map((l) => l.parameter_key);
  }

  loading = false;
  busy = false;
  error = '';
  preview: PreviewResult | null = null;

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;
    this.tenantId = Number(qp.get('tenantId'));
    this.productId = Number(qp.get('productId'));
    this.country = qp.get('country') ?? '';
    if (!this.tenantId || !this.productId || !this.country) {
      this.router.navigate(['/rule-packs']);
      return;
    }
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.error = '';
    this.rulesApi
      .getActivePackage(this.tenantId, this.productId, this.country)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.loading = false)))
      .subscribe({
        next: (env) => (this.config = (env.data?.configuration as Record<string, unknown>) ?? null),
        error: (e) => (this.error = resolveApiErrorMessage(e)),
      });
    this.rulesApi
      .getComplianceLocks(this.country)
      .pipe(assertSuccessEnvelope())
      .subscribe({ next: (env) => (this.locks = env.data ?? []), error: () => (this.locks = []) });
  }

  onValidate(cfg: Record<string, unknown>): void {
    this.busy = true;
    this.preview = null;
    this.rulesApi
      .previewPackage(this.tenantId, this.productId, this.country, cfg)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.busy = false)))
      .subscribe({
        next: (env) => (this.preview = env.data),
        error: (e) => (this.error = resolveApiErrorMessage(e)),
      });
  }

  onSaveDraft(cfg: Record<string, unknown>): void {
    this.busy = true;
    this.error = '';
    this.rulesApi
      .saveVersion(this.tenantId, this.productId, this.country, cfg)
      .pipe(assertSuccessEnvelope(), finalize(() => (this.busy = false)))
      .subscribe({
        next: () => this.back('draft'),
        error: (e) => (this.error = resolveApiErrorMessage(e)),
      });
  }

  onSavePublish(cfg: Record<string, unknown>): void {
    this.busy = true;
    this.error = '';
    this.rulesApi
      .saveVersion(this.tenantId, this.productId, this.country, cfg)
      .pipe(
        assertSuccessEnvelope(),
        switchMap((env) => this.rulesApi.publishVersion(env.data.id).pipe(assertSuccessEnvelope())),
        finalize(() => (this.busy = false)),
      )
      .subscribe({
        next: () => this.back('published'),
        error: (e) => (this.error = resolveApiErrorMessage(e)),
      });
  }

  back(saved?: 'draft' | 'published'): void {
    this.router.navigate(['/rule-packs'], { queryParams: { tenantId: this.tenantId, saved: saved ?? null } });
  }
}
